import type { Concept, Hour, Location } from "@mmh/types"

const SCHEMA_DAYS = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday"
] as const

type OpeningHoursSpecification = {
    "@type": "OpeningHoursSpecification"
    dayOfWeek: string[]
    opens: string
    closes: string
}

type PostalAddress = {
    "@type": "PostalAddress"
    streetAddress?: string
    addressLocality?: string
    addressRegion?: string
    postalCode?: string
    addressCountry: string
}

type RestaurantSchema = {
    "@context": "https://schema.org"
    "@type": "Restaurant"
    name: string
    image?: string
    url: string
    telephone?: string
    email?: string
    address?: PostalAddress
    geo?: {
        "@type": "GeoCoordinates"
        latitude: number
        longitude: number
    }
    openingHoursSpecification?: OpeningHoursSpecification[]
    servesCuisine?: string
    priceRange?: string
    hasMenu?: string
    menu?: string
    acceptsReservations?: string
    sameAs?: string[]
}

export type BuildRestaurantSchemaInput = {
    name: string
    image?: string
    url: string
    location: Location
    cuisine?: string
    socialMedia?: Concept["socialMedia"]
    menuUrl?: string
    reservationsUrl?: string
}

export function toOpeningHoursSpecification(hours: Hour[]): OpeningHoursSpecification[] | undefined {
    if (!hours.length) return undefined

    const timeRanges: Record<string, number[]> = {}
    for (const { days, times } of hours) {
        if (times.length < 2) continue
        const timeKey = times.join("-")
        timeRanges[timeKey] = [...(timeRanges[timeKey] || []), ...days]
    }

    const specs: OpeningHoursSpecification[] = []
    for (const [timeKey, days] of Object.entries(timeRanges)) {
        const [opens, closes] = timeKey.split("-")
        if (!opens || !closes) continue

        const uniqueDays = [...new Set(days)].sort((a, b) => a - b)
        const dayOfWeek = uniqueDays
            .filter(day => day >= 0 && day <= 6)
            .map(day => SCHEMA_DAYS[day])

        if (!dayOfWeek.length) continue

        specs.push({
            "@type": "OpeningHoursSpecification",
            dayOfWeek,
            opens,
            closes
        })
    }

    return specs.length ? specs : undefined
}

export function toSchemaPhone(phone?: string): string | undefined {
    if (!phone) return undefined

    const digits = phone.replace(/\D/g, "")
    if (digits.length === 10) {
        return `+1-${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`
    }
    if (digits.length === 11 && digits.startsWith("1")) {
        return `+1-${digits.slice(1, 4)}-${digits.slice(4, 7)}-${digits.slice(7)}`
    }

    return phone
}

export function toSameAs(socialMedia?: Concept["socialMedia"]): string[] | undefined {
    if (!socialMedia) return undefined

    const urls = [
        socialMedia.instagram?.url,
        socialMedia.tikTok?.url
    ].filter((url): url is string => Boolean(url))

    return urls.length ? urls : undefined
}

function toPostalAddress(location: Location): PostalAddress | undefined {
    const { address } = location
    if (!address) return undefined

    return {
        "@type": "PostalAddress",
        streetAddress: address.street,
        addressLocality: address.city,
        addressRegion: address.state,
        postalCode: address.zip,
        addressCountry: "US"
    }
}

export function buildRestaurantSchema(input: BuildRestaurantSchemaInput): RestaurantSchema | null {
    const { name, image, url, location, cuisine, socialMedia, menuUrl, reservationsUrl } = input
    if (!location) return null

    const address = toPostalAddress(location)

    if (location.closed) {
        return {
            "@context": "https://schema.org",
            "@type": "Restaurant",
            name,
            url,
            ...(address && { address })
        }
    }

    const geo = location.lat != null && location.lon != null
        ? {
            "@type": "GeoCoordinates" as const,
            latitude: location.lat,
            longitude: location.lon
        }
        : undefined

    const openingHoursSpecification = toOpeningHoursSpecification(location.hours ?? [])
    const telephone = toSchemaPhone(location.contact?.phone)
    const email = location.contact?.email
    const sameAs = toSameAs(socialMedia)

    return {
        "@context": "https://schema.org",
        "@type": "Restaurant",
        name,
        ...(image && { image }),
        url,
        ...(telephone && { telephone }),
        ...(email && { email }),
        ...(address && { address }),
        ...(geo && { geo }),
        ...(openingHoursSpecification && { openingHoursSpecification }),
        ...(cuisine && { servesCuisine: cuisine }),
        priceRange: "$$-$$$",
        ...(menuUrl && { hasMenu: menuUrl, menu: menuUrl }),
        ...(reservationsUrl && { acceptsReservations: reservationsUrl }),
        ...(sameAs && { sameAs })
    }
}
